// Tipos LOCALES del dominio `retailWizard` (pista Retail 🔵 · componente 05-F2).
//
// TD-F2-WIZ-001: migrar a `@fiado/type-kit/bin/retailWizard` cuando se habiliten las libs (hoy
// intactas — ver BITÁCORA F2 sesión 1). Estos tipos son fieles al catálogo de contratos de
// `pantallas-wizard.html` + `modelo-datos.md` v3 (congelado). Nombres en inglés (regla F2).
//
// El front habla EXCLUSIVAMENTE con el silo (regla puente D25): cada tipo aquí modela una
// respuesta/pedido de `retail-wizard-business`, NUNCA de un lambda Fiado.

import type { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import type { DeviceProvisioningTypeEnum } from '@fiado/type-kit/bin/mdm/enums/DeviceProvisioningTypeEnum';
import type { ProductComponent } from '@fiado/type-kit/bin/retailCatalog/dtos/ProductComponent';
import type { ItemTypeEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ItemTypeEnum';
import type { ProductCategoryEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductCategoryEnum';
import type { TrackingModeEnum } from '@fiado/type-kit/bin/retailCatalog/enums/TrackingModeEnum';
import type { WizardFinancingPreview } from '@fiado/type-kit/bin/retailWizard/dtos/WizardFinancingPreview';
import type { KycAttemptStageEnum } from '@fiado/type-kit/bin/retailWizard/enums/KycAttemptStageEnum';
import type { InventoryItemStatusEnum } from '@fiado/type-kit/bin/retailCatalog/enums/InventoryItemStatusEnum';
import type { WizardLineIdentifiers } from '@fiado/type-kit/bin/retailWizard/dtos/WizardLineIdentifiers';
import type { WizardLinePromotionFields } from '@fiado/type-kit/bin/retailWizard/dtos/WizardLinePromotionFields';
import type { CompleteSaleLine as CompleteSaleLineDto } from '@fiado/type-kit/bin/retailWizard/dtos/requests/CompleteSaleRequest';
import type { PinSessionPiecesLine } from '@fiado/type-kit/bin/retailWizard/dtos/requests/PinSessionPiecesRequest';
import type { components } from '@/api/services/retail-wizard/retail-wizard.gen';
import type { PaymentLine } from '@/shared/lib/format/salePaymentLines';
import type { LinePaymentTypeEnum } from '@fiado/type-kit/bin/retailWizard/enums/LinePaymentTypeEnum';
import type { WizardLineRoleEnum } from '@fiado/type-kit/bin/retailWizard/enums/WizardLineRoleEnum';
import type { WizardLineStatusEnum } from '@fiado/type-kit/bin/retailWizard/enums/WizardLineStatusEnum';
import type { MoneyAtStakeKindEnum } from '@fiado/type-kit/bin/retailWizard/enums/MoneyAtStakeKindEnum';

// ── Pasos del wizard ─────────────────────────────────────────────────────────
// Los pasos del silo. En venta de CONTADO, 05/06/08 (crédito, pista SOFOM) van `skipped`.
export type WizardStepId =
    | '01' // OTP + Términos
    | '02' // KYC
    | '03' // Elige el teléfono y Arma la venta
    | '04' // Forma de pago
    | '05' // Score        (skipped en contado — pista SOFOM)
    | '06' // Cotización    (skipped en contado — pista SOFOM)
    | '07' // Cobro
    | '08' // Firma        (skipped en contado — pista SOFOM)
    | '09' // Entrega
    | '10' // Bienvenida N2
    | '11'; // Referidos (tras la Entrega, antes de la Bienvenida)

/** Slug de ruta por paso (URL `/wizard/s/:id/<slug>`). `product` y `build-sale` comparten el 03. */
export type WizardStepSlug =
    | 'otp'
    | 'kyc'
    | 'product'
    | 'build-sale'
    | 'payment-method'
    | 'score'
    | 'quote'
    | 'payment'
    | 'sign'
    | 'referrals'
    | 'delivery'
    | 'welcome'
    | 'summary';

/** Pantallas a las que puede volver una venta guardada como prospecto (`resumeSlug` del silo). */
export const RESUME_SLUGS: readonly WizardStepSlug[] = [
    'otp',
    'kyc',
    'score',
    'product',
    'build-sale',
    'payment-method',
    'quote',
    'payment',
    'sign',
    'referrals',
    'delivery',
    'welcome',
];

/**
 * Por qué el silo manda al cliente a ese paso al reanudar. Busca el PRIMER hueco del onboarding, de
 * arriba hacia abajo: cuenta → KYC → domicilio → preguntas.
 */
export const RESUME_REASONS = [
    'ACCOUNT_READY',
    'KYC_PENDING',
    'ADDRESS_PENDING',
    'PROFILE_PENDING',
    'ACCOUNT_PENDING',
] as const;

export type ResumeReason = (typeof RESUME_REASONS)[number];

/** A dónde vuelve el cliente al reanudar. Lo calcula el silo; este front solo obedece el `step`. */
export interface ResumeTarget {
    step: WizardStepId;
    reason: ResumeReason;
}

// ── Estado de la saga (WizardSession) ────────────────────────────────────────
export type WizardSessionState =
    | 'ACTIVE'
    | 'PAUSED_EXTERNAL_REFERRAL' // derivación a financiera externa (04)
    | 'PAUSED_NO_TOPUP' // salió sin recarga (10) — venta completa, wallet a medias
    | 'PAUSED_CURP_CONFLICT' // el CURP verificado ya es de otro cliente (02) — la venta no puede seguir
    | 'COMPLETED'
    | 'CANCELLED';

/** Gate de identidad Fiado (D27) resuelto server-side dentro de `POST /wizard/sessions`. */
export type PhoneStatus = 'NEW' | 'RECOGNIZED';

/** Modalidad de captura KYC (D18). */
export type KycModality = 'STORE_DEVICE' | 'CUSTOMER_LINK';

/** Estados de KYC que renderiza la pantalla 02 (según `GET /wizard/kyc/status`). */
export type KycStatus =
    | 'READY'
    | 'WAITING'
    | 'VERIFIED'
    | 'FAILED'
    | 'REVIEW' // en revisión de Cumplimiento — la venta CONTINÚA
    | 'MANUAL' // captura manual (sin cuenta PCF) — degrada 09/10
    | 'PLD'; // bloqueo definitivo → sesión CANCELLED

/**
 * Apertura de cuenta PCF async (D35) — se lee de NUESTRO silo, jamás de Fiado.
 * `LINKED`: el comprador ya traía cuenta PagoConFiado y se enlazó la suya; no hay apertura que esperar.
 */
export type AccountStatus = 'OPENING' | 'OPEN' | 'FAILED' | 'LINKED';

/** Qué hace la pantalla con la apertura. Es el único campo del estatus que decide el flujo del paso 02. */
export type AccountOutcome = 'READY' | 'IN_PROGRESS' | 'MISSING_INFO' | 'CANNOT_OPEN';

/** `BlockedReasonEnum` del silo: `ACCOUNT_OPENING` es la espera normal, `ACCOUNT_BLOCKED` la que ya no cede sola. */
export type BlockedReason = 'ACCOUNT_OPENING' | 'ACCOUNT_BLOCKED';

/** `BlockedCategoryEnum` del silo: quién resuelve. Es lo único del motivo que se muestra, y solo en Prospectos. */
export type BlockedCategory = 'CUSTOMER_RECORD' | 'UNDER_REVIEW' | 'TEMPORARY' | 'REJECTED';

/**
 * `WizardSession.blocked` — la venta detenida porque la cuenta no está lista. Ausente = va bien.
 * 🔴 `code` y `detail` llegan en la respuesta y el mapper no los propaga: nombrar la causa es delito.
 */
export interface WizardBlocked {
    reason: BlockedReason;
    /** `null` durante la espera normal: todavía no hay bloqueo que categorizar. */
    category: BlockedCategory | null;
    /** Desde cuándo lleva trabada, ISO 8601. Es el «hace N días» de Prospectos. */
    since: string;
}

/** Lo mínimo del bloqueo que necesita la ficha de Prospectos: la etiqueta y el «hace N días». */
export interface WizardBlockedSummary {
    category: BlockedCategory | null;
    since: string;
}

/** `GET …/account-status` — si la cuenta PagoConFiado ya existe, y qué pasa mientras no. */
export interface AccountStatusResult {
    /** Hay cuenta ACTIVE ⇒ se puede avanzar. Es lo único que desbloquea el paso. */
    accountReady: boolean;
    outcome: AccountOutcome;
}

/** Método de cobro de contado (07). */
export type PaymentMethod = 'CASH' | 'CARD' | 'SPEI';

/**
 * Ingreso mensual declarado (02 y 10) — catálogo cerrado `MonthlyIncomeMXN` de Fiado. Cada valor es
 * un TOPE, no un monto exacto. Las etiquetas viven en `profileCatalog.ts`.
 */
export type MonthlyIncome = '0' | '10000' | '20000' | '50000' | '100000' | '150000';

/** Ocupación (02) — 7 valores; OTHER abre texto libre ≤60 (Manual v1.4 §8.13). */
export type Occupation =
    | 'EMPLOYED'
    | 'SELF_EMPLOYED'
    | 'STUDENT'
    | 'HOMEMAKER'
    | 'RETIRED'
    | 'UNEMPLOYED'
    | 'OTHER';

// ── Entidades de vista (lo que el front consume del silo) ─────────────────────

/** Nivel SCI del cliente (`ClientLevelEnum` del backend) — chip de nivel de los pasos 04-09. */
export type ClientLevel = 'NOT_ELIGIBLE' | 'BRONZE' | 'SILVER' | 'GOLD';

/**
 * Contexto de crédito de la sesión (bloque `loan` — pista SOFOM, pasos 04-09). Solo PUNTEROS al
 * dominio loan-*; `level` es la única denormalización (chip de nivel sin refetch). `undefined` en
 * ventas de contado/externa.
 */
export interface WizardLoanContext {
    borrowerId: string;
    sciEvaluationId?: string;
    level?: ClientLevel;
    planId?: string;
    creditId?: string;
}

/**
 * Opción de financiamiento del paso 04 (`GET /financiers`): catálogo SOFOM × elegibilidad del
 * producto reservado. INTERNAL = SureKeep (pista crédito propia); EXTERNAL = derivación.
 */
export interface FinancierOption {
    code: string;
    name: string;
    /** Texto de display de la card (viene del catálogo SOFOM). */
    label: string;
    type: 'INTERNAL' | 'EXTERNAL';
    displayOrder: number;
    /** Solo EXTERNAL: URL de derivación. */
    externalRedirectUrl?: string;
    /** Se puede elegir: el producto la admite y, en INTERNAL, el cliente no tiene crédito vivo. */
    eligible: boolean;
    /** Por qué el servidor la marca no elegible; ausente si se puede elegir. */
    ineligibleReason?: FinancierIneligibleReason;
}

/** Motivo tipado de una financiera no elegible (`FinancierIneligibleReasonEnum` del backend). */
export type FinancierIneligibleReason = 'ACTIVE_CREDIT';

/**
 * Decisión del score SCI (paso 05): `POST …/score/consult` la devuelve DIRECTA (idempotente por
 * sesión); `GET …/score/result` la relee del snapshot al reanudar. La oferta puede venir nula
 * (camino Círculo+SCI): las condiciones exactas las resuelve el plan en la cotización (06).
 */
export interface ScoreDecision {
    status: 'APPROVED' | 'REJECTED' | 'NO_ELIGIBLE';
    level: ClientLevel;
    sciEvaluationId: string;
    /** ⚠️ Unidades de creditLine/downPayment según proveedor (TD Modelias) — no asumir cents. */
    offer?: {
        creditLine?: number;
        downPayment?: number;
        termsWeeks: number[];
        rates: Record<string, number>;
    };
}

/** Bounds del plan que gobiernan el cotizador del paso 06 (vienen en la respuesta del quote). */
export interface QuotePlan {
    planId: string;
    code: string;
    /** Enganche mínimo como decimal sobre 1 (`0.20` = 20%). */
    minDownPaymentPct: number;
    /** Techo de enganche (decimal sobre 1); `undefined` = sin techo del plan. */
    maxDownPaymentPct?: number;
    termWeeksOptions: number[];
    /** TNA decimal (`2.00` = 200%) — la tasa que manda es la del PLAN. */
    tnaAnnual: number;
    openingCommissionPct: number;
}

/** Resumen financiero del crédito originado/re-cotizado (montos en CENTS; fuente: loan-credit). */
export interface CreditSummary {
    creditId: string;
    status: string;
    planId: string;
    termWeeks: number;
    equipmentPriceCents: number;
    downPaymentCents: number;
    financedCapitalCents: number;
    tnaAnnual: number;
    weeklyPaymentCents: number;
    /** Cuota semanal con IVA — el número que ve el cliente. */
    weeklyPaymentWithIvaCents: number;
    totalInterestCents: number;
    totalIvaCents: number;
    totalToPayCents: number;
}

/**
 * Cotas con las que el motor cotizó de verdad, ya pisadas por las promociones y las políticas de la
 * tienda. NO son las del plan: el control del enganche se dibuja con estas cuando llegan.
 */
export interface EffectiveTerms {
    /** Enganche mínimo exigible, decimal sobre 1 (`0.40` = 40%). */
    minDownPaymentPct: number;
    /** Techo de enganche (decimal sobre 1); `null` = sin techo. */
    maxDownPaymentPct: number | null;
    /** Techo de capital financiable. Acota el enganche por abajo: lo que no se financia se paga. */
    maxFinancedAmountCents?: number | null;
}

/** `POST …/quote` — 1ª llamada ORIGINA el crédito (idempotente por sesión); siguientes RE-COTIZAN. */
export interface QuoteResult {
    plan: QuotePlan;
    credit: CreditSummary;
    /** Cotas reales de esta cotización; ausente si el silo todavía no las emite. */
    effectiveTerms?: EffectiveTerms;
    /** Bono de la promo de crédito ganadora en esta cotización; ausente si no hay. */
    creditPromotionBonusCents?: number;
}

/**
 * Las 4 condiciones de activación del crédito (type-kit `loanCredit/ActivationChecklist`): el
 * crédito pasa a ACTIVE solo con las 4 en true. El enganche (07) marca la 1ª; la firma (08) la 2ª;
 * el enrolamiento MDM de la entrega (09) las otras dos.
 */
export interface ActivationChecklist {
    downPayment: boolean;
    contractSigned: boolean;
    imeiEnrolled: boolean;
    lockVerified: boolean;
}

/**
 * `POST …/down-payment` (paso 07, crédito) — el folio `SK-…-ENG-…` lo genera el SERVER dentro de
 * este endpoint (no existe pre-generación en la pista crédito; el `/payment/folio` es del contado
 * y produce otro formato). `checklist` = estado post `activate-check { downPayment: true }`.
 */
export interface DownPaymentResult {
    paymentId: string;
    folio: string;
    registeredAt: string;
    checklist: ActivationChecklist;
}

/**
 * `GET …/contract/preview` (paso 08; también rehidrata el 07 al reanudar) — resumen del crédito +
 * variables del contrato. `contractData` es PII TRANSITORIA: viaja solo en esta response, no se
 * persiste ni se loggea en el front. Llaves conocidas: nombreAcreditado · curp · domicilio ·
 * lugarFirma (CONTRACT_DATA_KEYS del wizard).
 */
export interface ContractPreview {
    credit: CreditSummary;
    contractData: Record<string, string>;
}

/**
 * `GET …/contract/document` — enlace firmado y de vida corta al PDF del contrato: vista previa antes
 * del código (`preview: true`) y el que se firma después.
 */
export interface ContractDocumentLink {
    url: string;
    expiresAt: string;
    preview: boolean;
}

/**
 * `POST …/signature/send-otp` — firma FES tiempo 1 (paso 08): el motor congela términos, genera
 * los PDFs y despacha el OTP dedicado de firma (4 dígitos, 5 min) al celular del cliente.
 * Re-llamarlo REENVÍA un código nuevo.
 */
export interface SignatureSendOtpResult {
    otpSent: boolean;
    /** Teléfono destino enmascarado (`****1234`) — nunca viaja el número completo. */
    otpDestinationMasked: string;
}

/**
 * `POST …/signature/sign` — firma FES tiempo 2: verifica el OTP y firma. `signed: true` implica
 * `checklist.contractSigned: true` (condición 2 de activación). Errores: OTP_INVALID (422) ·
 * OTP_EXPIRED (422) · OTP_MAX_TRIES (422).
 */
export interface SignatureSignResult {
    signed: boolean;
    checklist: ActivationChecklist;
}

/**
 * `POST …/device` (paso 09, crédito) — tiempo 1: registra el IMEI (15 díg; Luhn y unicidad los
 * valida loan-credit) y ABRE el enrolamiento, que nace `PENDING`. No enrola nada por sí solo: el
 * candado lo instala una persona en el equipo, y `lockAppUrl` es el APK que ahí se baja.
 * Errores: INVALID_IMEI · IMEI_IN_USE · RESERVATION_EXPIRED · DEVICE_ENROLLMENT_FAILED · PRODUCT_WITHOUT_MDM.
 */
export interface RegisterDeviceResult {
    enrollment: DeviceEnrollment;
    /** URL del APK del candado — sale del silo por ambiente (el de UAT no es el de producción). */
    lockAppUrl: string;
    /** Proveedor de MDM del equipo; el client lo narra. Ausente = proveedor desconocido o sin MDM. */
    mdmProvider?: MdmProviderEnum;
}

/**
 * Los cuatro desenlaces del enrolamiento. `PROVIDER_UNREACHABLE` NO es un `PENDING` lento: dice que
 * no pudimos preguntar, y el vendedor tiene que poder distinguirlo de que le falte un paso.
 */
export type DeviceEnrollmentStatus = 'PENDING' | 'ENROLLED' | 'PROVIDER_UNREACHABLE' | 'FAILED';

/** Por qué falló el enrolamiento; solo viaja con `FAILED`. */
export type DeviceEnrollmentFailureReason =
    | 'MODEL_NOT_SUPPORTED'
    | 'ALREADY_IN_PROVIDER'
    | 'PROVIDER_REJECTED';

/** Code del rechazo del candado; el mismo que el error del alta. Solo viaja con `FAILED`. */
export type DeviceEnrollmentFailureCode =
    | 'INVALID_IMEI'
    | 'MDM_DEVICE_REGISTERED_ELSEWHERE'
    | 'MDM_DEVICE_MODEL_NOT_SUPPORTED'
    | 'MDM_PROVIDER_CONFIG_ERROR'
    | 'MDM_ENROLL_REJECTED';

export interface DeviceEnrollment {
    status: DeviceEnrollmentStatus;
    imei: string;
    failureReason?: DeviceEnrollmentFailureReason;
    /** Manda sobre `failureReason` para elegir el consejo. */
    failureCode?: DeviceEnrollmentFailureCode;
    /** Si el vendedor puede reintentarlo desde el wizard. Ausente = no se puede. */
    retryable?: boolean;
}

/**
 * `GET …/device/enrollment` — tiempo 2, polleado mientras el vendedor instala el candado.
 *
 * 🔴 `ENROLLED` SIN `creditStatus` no es el final: el proveedor registró el equipo pero el candado
 * todavía no dio señal de vida. Es el hueco normal entre las dos cosas y dura minutos — se sigue
 * polleando. Solo con `creditStatus` poblado el silo ya corrió el activate-check.
 */
/**
 * Los tres desenlaces del bono PCF de una promoción. `SETTLED` es el único donde de verdad se movió
 * dinero — es lo que separa una celebración de una mentira.
 */
export type PromotionBonusStatus = 'SETTLED' | 'FAILED' | 'UNVERIFIED';

/** Estado del bono de crédito persistido en la sesión: `RESERVED` = en proceso. */
export type CreditPromotionBonusStatus = 'RESERVED' | PromotionBonusStatus;

/** Bono de la promo de crédito tal como lo guarda el silo; sobrevive a recargar y a reanudar. */
export interface CreditPromotionBonusState {
    promotionId: string;
    amountCents: number;
    status: CreditPromotionBonusStatus;
    /** ISO del instante en que entró en RESERVED; lo sella el servidor y ancla la ventana del reintento. */
    pendingSince?: string;
}

/** Lo que pasó con el bono en ESTE tick del poll. Ausente = no hubo bono que pagar. */
export interface PromotionBonusOutcome {
    status: PromotionBonusStatus;
    amountCents: number;
}

export interface DeviceEnrollmentResult {
    /** `null` = el silo todavía no tiene expediente para esta sesión: nadie registró un IMEI. */
    enrollment: DeviceEnrollment | null;
    /** Viaja SIEMPRE, también antes de que exista enrolamiento: el QR no depende del POST. */
    lockAppUrl: string;
    /**
     * Proveedor de MDM del equipo, y con él el flujo del paso 09. Viaja también antes de registrar
     * el IMEI. Ausente = producto sin MDM, catálogo caído o SKU sin resolver.
     */
    mdmProvider?: MdmProviderEnum;
    /** Método de alta del equipo; decide qué pasos se muestran en el teléfono. */
    /** KNOX_GUARD o PAY_TRIGGER; ausente = no llegó o no lo reconocemos. */
    provisioningType?: DeviceProvisioningTypeEnum;
    creditStatus?: string;
    checklist?: ActivationChecklist;
    missingItems?: string[];
    /** Viaja SOLO en el tick que cerró el asiento: la pantalla lo celebra una vez, no en cada poll. */
    promotionBonus?: PromotionBonusOutcome;
    /** Bono de crédito persistido; mientras esté RESERVED y en ventana, el poll sigue. */
    creditPromotionBonus?: CreditPromotionBonusState;
    /** Venta que cerró el servidor en este poll (crédito ACTIVE con piezas fijadas). */
    saleId?: string;
}

/** Estado del correo del paso 01 (`EmailStateEnum` del silo). */
export type EmailState = 'EXISTING' | 'CAPTURED' | 'PENDING';

/** Tipo del correo (`EmailTypeEnum` de contacts). */
export type EmailKind = 'PERSONAL' | 'CORPORATE';

/** Correo de la venta. `address` solo viene si lo capturó el vendedor; si no, `masked`. */
export interface SessionEmail {
    state: EmailState;
    kind?: EmailKind;
    masked?: string;
    address?: string;
}

/** Correo que el silo rechazó por ser de otro cliente; se guarda antes de releer la sesión. */
export type TakenEmail = Pick<SessionEmail, 'address' | 'kind'>;

/** Motivos con los que el sistema cancela una apertura de cuenta (`SystemCancelReasonEnum`). */
export type AccountOpeningCancelReason = 'ALREADY_HAS_ACCOUNT' | 'CURP_OF_OTHER_CUSTOMER';

export interface WizardSession {
    sessionId: string;
    state: WizardSessionState;
    /** La venta cerró por inactividad (`EXPIRED` del silo); `state` la muestra como cancelada. */
    expired?: boolean;
    /** La cerró el barrido porque loan-credit descartó la cotización. */
    quoteDiscarded?: boolean;
    /** La canceló el sistema porque otra sucursal tomó al cliente. */
    takenOverByOtherStore?: boolean;
    /** Apertura de cuenta sin venta (D-01). Ausente = venta. */
    accountOpening?: true;
    /** Por qué el sistema canceló la apertura; ausente en cualquier otro cierre. */
    accountOpeningCancelReason?: AccountOpeningCancelReason;
    currentStep: WizardStepId;
    paymentMethodKind: 'CASH_OR_CARD' | 'SUREKEEP' | 'EXTERNAL' | null;
    /** Código de la financiera a la que se derivó la venta. */
    externalProvider?: string;
    /** Contexto de crédito (pista SOFOM, pasos 04-09). `undefined` en contado/externa. */
    loan?: WizardLoanContext;
    phone: string;
    phoneStatus: PhoneStatus;
    firstName?: string; // saludo "Cliente reconocido: Juan" (D27)
    otpVerified: boolean;
    otpSkipped: boolean;
    /** Hay una reanudación esperando su OTP, aunque `otpVerified` siga en `true` de antes. */
    resumeOtpRequired: boolean;
    /** Primer hueco del onboarding: a qué paso aterriza el cliente al reanudar. Lo decide el silo. */
    resumeTarget?: ResumeTarget;
    /** Pantalla desde la que el vendedor guardó y salió. Si viene, manda sobre `resumeTarget`. */
    resumeSlug?: WizardStepSlug | null;
    /** Solo de contado: el cliente tiene una venta con crédito en curso en otra sucursal. */
    cashOnly?: boolean;
    consentsAccepted: boolean;
    /** Correo del cliente del paso 01. Ausente en ventas anteriores al correo. */
    email?: SessionEmail;
    kyc: KycState;
    occupation?: { code: Occupation; otherText?: string };
    /** Espejo del ingreso del paso 02. El dato vive en People de Fiado; el silo solo lo refleja. */
    monthlyIncome?: MonthlyIncome;
    accountStatus: AccountStatus;
    /**
     * La venta está detenida esperando la cuenta. Es un CAMPO y no un estado: `state` sigue `ACTIVE`,
     * así el retorno con OTP y los barridos de mantenimiento siguen corriendo.
     *
     * Ausente = la venta va bien. El silo lo manda `null`; el mapper normaliza, como con `product`.
     */
    blocked?: WizardBlocked;
    /** Espejo de la línea financiada o la principal; en sesiones con líneas puede venir sin IMEI. */
    product?: SelectedProduct;
    /** Líneas de la venta. Ausente en las sesiones anteriores a las líneas. */
    reservedLines?: ReservedLine[];
    /** Ancla que fija el servidor. Ausente en contado o sin líneas financiables. */
    financedLineId?: string;
    /** Promo de venta principal; ausente con «Ninguna». */
    salePromotion?: SalePromotionRef;
    /** Todas las promos de venta fijadas; en sesiones viejas sale de `salePromotion`. */
    salePromotions?: SalePromotionRef[];
    /** Reparto provisional antes de cotizar; solo con el reparto prendido. */
    financingPreview?: FinancingPreview;
    payment?: RegisteredPayment;
    folio?: string; // D11 — server-side
    saleId?: string;
    /** Todas las piezas rastreadas quedaron HELD con /pieces; ausente = cierre con las lines de hoy. */
    piecesHeldAt?: string;
    delivery: DeliveryState;
    welcome: WelcomeState;
    /** Bono de la promo de crédito; ausente mientras no se reserve. */
    creditPromotionBonus?: CreditPromotionBonusState;
    storeId: string;
    sellerId: string;
    /** Vencimiento de la reserva/sesión (D14): reserva 30 min · reanudable 24 h. */
    reservationExpiresAt?: string;
    updatedAt: string;
}

/**
 * Las 8 estaciones del KYC, en el orden en que el cliente las vive.
 *
 * ⚠️ TD-F2-WIZ-001 — esto duplica a mano `Kyc.KycSubCheckNameEnum` de `@fiado/type-kit`, cuyo propio
 * docstring avisa que un desalineo seria "una falla muda". Hoy NO se puede importar: este webapp fija
 * `^3.194.0` y el modulo `kyc` no existe en esa version. **Al hacer el bump, esta constante muere** —
 * junto con los tipos de abajo.
 */
export const KYC_ESTACIONES = [
    'documentReading',
    'templateMatching',
    'alterationDetection',
    'liveness',
    'facematch',
    'ageCheck',
    'watchlists',
    'premiumAmlWatchlists',
] as const;

export type KycEstacion = (typeof KYC_ESTACIONES)[number];

/** Estaciones que hablan `CLEAR`/`HIT`. Las otras 6 hablan `PASS`/`FAIL`. */
export type KycEstacionDeLista = 'watchlists' | 'premiumAmlWatchlists';

/**
 * Resultado de cada estación, tal como lo publica el silo (`GET /kyc/status`).
 *
 * Una estación AUSENTE significa "todavía no cerró" o "no corre en este flujo" — distinto de un
 * fallo. El objeto entero es `null` mientras no cerró ninguna.
 *
 * 🔴 Los dos vocabularios NO se aplanan en una sola unión de 4 valores, aunque seria mas corto: eso
 * aceptaria `watchlists: 'PASS'` y `liveness: 'HIT'`, y desarmaria el candado que `@fiado/type-kit`
 * puso a proposito. Un `HIT` **no es un fallo** — es "que lo mire un oficial de cumplimiento" —, y si
 * los dos vocabularios se mezclan, la venta se bloquea sola sin revision.
 */
export type KycSubChecks = Partial<
    Record<Exclude<KycEstacion, KycEstacionDeLista>, 'PASS' | 'FAIL'>
> &
    Partial<Record<KycEstacionDeLista, 'CLEAR' | 'HIT'>>;

/**
 * Ruta del comprador tras la bifurcación del paso 01d.
 *
 * - **FACEMATCH** — ya tiene cuenta PagoConFiado: solo se toma una selfie, que se compara contra la
 *   foto que ya dio. No necesita su INE.
 * - **FULL_KYC** — no tiene cuenta: escanea la INE por ambos lados y se toma una selfie.
 *
 * Cambia lo que el vendedor tiene que pedirle al cliente, así que la pantalla del paso 02 se pinta
 * distinta según cuál sea.
 */
export type CustomerRoute = 'FACEMATCH' | 'FULL_KYC';

/**
 * Los 12 desenlaces del comprobante de domicilio (`Kyc.ProofAddressStatusEnum` del type-kit).
 *
 * Contestan "¿pudimos usar el comprobante?", que NO es lo mismo que "¿el proveedor aprobó el
 * documento?". Cada valor pide algo distinto del vendedor — ver `proofOfAddress.ts`.
 */
export const PROOF_ADDRESS_STATUSES = [
    'SUCCESS',
    'MEX_HELP_NEEDED',
    'USA_HELP_NEEDED',
    'COUNTRY_MISMATCH',
    'COUNTRY_NOT_SUPPORTED',
    'PENDING_METAMAP_REVIEW',
    'NO_ADDRESS_DETECTED',
    'PLACES_NO_MATCH',
    'METAMAP_ERROR',
    'PLACES_ERROR',
    'ADDRESS_SAVE_ERROR',
    'CHECKBOX_ERROR',
] as const;

export type ProofAddressStatus = (typeof PROOF_ADDRESS_STATUSES)[number];

/**
 * `UNKNOWN` NO viene del silo: es lo que este front hace con un valor que no conoce. El catálogo
 * crece del lado del proveedor, y descartar el resultado en silencio se leería como "todavía no
 * cerró" — o sea, como que la venta puede seguir. Se dice que no sabemos.
 */
export type ProofAddressResultStatus = ProofAddressStatus | 'UNKNOWN';

/**
 * Cómo quedó el comprobante de domicilio. Viaja en el POLL de `kyc/status`; `null` mientras no cerró.
 *
 * ⚠️ `addressPersisted` sirve para reaccionar rápido, NO para afirmar que hay domicilio:
 * `COUNTRY_NOT_SUPPORTED` puede venir en `true` sin que se haya guardado nada. La fuente de verdad
 * es la lista de domicilios del perfil.
 */
export interface ProofOfAddressResult {
    status: ProofAddressResultStatus;
    addressPersisted: boolean;
    verificationId: string;
    /**
     * ¿Este comprobante ya cerró? Lo decide el SILO, que es quien conoce el ciclo de vida de cada
     * status; el front lo copia. `undefined` solo en la ventana de despliegue, mientras un wizard
     * viejo responda sin el campo.
     */
    terminal?: boolean;
}

/**
 * `POST …/proof-of-address/prepare` — gemelo de `KycPrepareResponse` para el flow del COMPROBANTE de
 * domicilio, que es distinto del documento de identidad. Bifurca por modalidad.
 */
export interface ProofOfAddressPrepareResponse {
    modality: KycModality;
    /** STORE_DEVICE: config del widget SaaS de Metamap. */
    clientId?: string;
    flowId?: string;
    metadata?: string;
    /** CUSTOMER_LINK: liga corta para el QR. */
    shortUrl?: string;
    /** El expediente ya tiene domicilio principal ⇒ no hay comprobante que pedir. */
    alreadyHasAddress?: boolean;
    /** Intento que abrió este prepare; el front liga el SDK y el QR a él. Ausente si no hubo intento. */
    attemptId?: string;
}

/** `kycFailure.code` del silo que la pantalla sabe explicar. */
export const KYC_FAILURE_CODES = [
    'FACEMATCH_NO_MATCH',
    'FACEMATCH_REJECTED',
    'FACEMATCH_EXPIRED',
    'FACEMATCH_PROVIDER_FAILED',
    'FACEMATCH_PREPARE_UNAVAILABLE',
    'FACEMATCH_SETUP_FAILED',
    'FIADO_IDENTITY_INCOMPLETE',
    'FIADO_CURP_MISMATCH',
] as const;

/**
 * Motivo por el que la verificación no avanzó. `REGISTRATION_PENDING` es una falla transitoria del
 * alta que el silo reintenta solo; `UNKNOWN` es cualquier code que la pantalla no conoce.
 */
export type KycFailure = (typeof KYC_FAILURE_CODES)[number] | 'REGISTRATION_PENDING' | 'UNKNOWN';

/** Flujo del paso 02 con intento propio de MetaMap; son las llaves de `progress` en `kyc/status`. */
export type MetamapFlow = 'identity' | 'facematch' | 'proofOfAddress';

/** Intento vigente de un flujo (`RetailWizard.KycAttemptProgress`): de aquí salen la etapa y el QR. */
export interface KycAttemptProgress {
    attemptId: string;
    modality: KycModality;
    /** `shortUrl` del QR; `null` en la tablet o si el silo no lo entregó. */
    link: string | null;
    stage: KycAttemptStageEnum;
    stageAt: string;
    /** Fecha del prepare (ISO); el tope de 25 min se mide contra ella, así recargar no lo reinicia. */
    attemptStartedAt: string;
}

/** `RetailWizard.KycStatusProgress`: un intento por flujo, `null` si el flujo no tiene. */
export type KycProgress = Record<MetamapFlow, KycAttemptProgress | null>;

export interface KycState {
    status: KycStatus;
    modality?: KycModality;
    /** `undefined` mientras la bifurcación no se resolvió: no se asume ninguna de las dos. */
    route?: CustomerRoute;
    retryCount: number;
    /**
     * Avance estación por estación. Llega por el poll de `kyc/status` conforme Metamap va cerrando
     * cada una — no de golpe al final.
     *
     * 🔴 **No es el veredicto.** Un `FAIL` aquí no frena la venta: el cliente reintenta en el mismo
     * dispositivo, sin salir de la sesión. Por eso la UI lo pinta en ÁMBAR y no en rojo. El rojo se
     * reserva para `status: 'FAILED' | 'PLD'`, que sí es la decisión final.
     */
    subChecks?: KycSubChecks | null;
    /**
     * Lo que el proveedor extrajo del INE, para que el vendedor lo confirme antes de seguir. Llega por
     * el POLL de `kyc/status`, no por la sesión: el silo es PII-zero y no persiste identidad ni
     * domicilio — los lee del customer al responder.
     *
     * 🔴 Todo lo de adentro es opcional MENOS `fullName`. El OCR de una credencial es best-effort y un
     * campo que no se extrajo llega ausente: la pantalla lo dice ("no se pudo leer") en vez de pintar
     * un vacío que el vendedor leería como "está bien".
     */
    verified?: {
        fullName: string;
        curp?: string;
        birthDate?: string;
        /** Vigencia del documento con el que se verificó. Ausente si el OCR no la pudo leer. */
        documentExpiresAt?: string;
        /**
         * Tipo de documento, crudo del proveedor (`national-id`, `passport`, …). Ausente en los
         * clientes verificados antes de que el dato viajara — ahí la pantalla no lo nombra, que es
         * preferible a nombrar el equivocado.
         */
        documentType?: string;
        /** Domicilio en una línea, ya compuesto por el silo. Ausente si el documento no traía uno legible. */
        addressText?: string;
        /** `true` si el cliente ya lo confirmó (paso 09). En el paso 02 siempre es `false`. */
        addressConfirmed?: boolean;
    };
    failureReason?: string;
    retryable?: boolean;
    /** Motivo de `kyc/status`; manda sobre `status`. `null` = no hay falla anotada. */
    failure?: KycFailure | null;
    /**
     * Cómo cerró el comprobante de domicilio. Sale de la sesión en el mismo poll, sin costo de red
     * extra. `null` mientras no cerró ninguno.
     */
    proofOfAddress?: ProofOfAddressResult | null;
    /**
     * ¿El silo va a exigir domicilio al guardar el perfil? Es su propio predicado, no una deducción
     * de este front. `undefined` = despliegue que todavía no lo manda; ahí no se bloquea nada.
     */
    addressRequired?: boolean;
    /** Intento vigente por flujo. Ausente en un wizard previo al despliegue. */
    progress?: KycProgress;
}

export interface DeliveryState {
    cardDelivered: boolean;
    /**
     * El comprador YA trae una tarjeta física PagoConFiado vigente en México, así que no hay plástico
     * que entregarle. Es una señal DISTINTA de tener cuenta: se puede abrir la cuenta desde la app y
     * recibir la primera tarjeta después, en el mostrador — por eso el silo la pregunta en vez de
     * deducirla de la ruta del KYC.
     */
    cardAlreadyOwned: boolean;
    /** Motivos por los que una tarea quedó en bandeja Pendientes (D13). */
    pendingReasons: string[];
}

/** Las 4 condiciones que el cliente cumple desde su app Fiado (paso 09). */
export type AppOnboardingStep = 'PASSWORD' | 'CONTRACT_SIGNED' | 'SECRET_NUMBER' | 'PHYSICAL_CARD';

/**
 * Tres estados, no un booleano: `PENDING` es "el cliente todavía no lo hace" y `UNVERIFIABLE` es
 * "se cayó una lectura NUESTRA". Aplastarlos deja al vendedor esperando a un cliente que ya cumplió.
 */
export type AppOnboardingStatus = 'COMPLETED' | 'PENDING' | 'UNVERIFIABLE';

export interface AppOnboardingStepState {
    /** Las 4 conocidas + lo que el silo agregue. Abierto a propósito: no se valida contra una lista. */
    step: string;
    status: AppOnboardingStatus;
    /** Qué lectura falló, escrito para el vendedor. Solo viene con `UNVERIFIABLE`. */
    reason?: string;
}

export interface AppOnboardingState {
    steps: AppOnboardingStepState[];
    /** Ninguna condición quedó `PENDING`. Lo decide el silo — `UNVERIFIABLE` no bloquea. */
    allSettled: boolean;
    /** La tienda tiene tarjetas físicas; `null` si el silo no lo supo. Con `false` la tarjeta no aplica. */
    cardStockAvailable?: boolean | null;
}

export interface WelcomeState {
    addressConfirmed: boolean;
    /** El wire real solo persiste la BANDERA (TD-003: el beneficiario aún no viaja al expediente). */
    benefCaptured: boolean;
    /** Detalle local/mock — el silo NO lo devuelve; para display inmediato tras capturarlo. */
    beneficiary?: Beneficiary;
    annexSigned: boolean;
    topupApplied: boolean;
    level2: boolean;
    welcomeAccredited: boolean;
}

/**
 * Estado del perfil del paso 10 (`GET /wizard/sessions/{id}/profile`). Lo arma el wizard leyendo la
 * FUENTE — People de Fiado y address-lambda —, no el espejo de la sesión.
 */
export interface ProfileState {
    preguntas: ProfileAnswers;
    /** Las seis contestadas. La pantalla las muestra igual, para poder corregirlas. */
    preguntasCompletas: boolean;
    domicilios: SavedAddress[];
    /**
     * Los que Fiado ya tiene. `soportado` distingue "no tiene" de "no puedo saberlo": con lista
     * vacía la pantalla ofrecería un segundo beneficiario a quien ya tiene uno, y el destino ACUMULA.
     */
    beneficiarios: { soportado: boolean; items: SavedBeneficiary[] };
}

/** Las seis del perfil del paso 10: las cinco de PLD más la ocupación. Sin contestar viajan `undefined`. */
export interface ProfileAnswers {
    occupation?: Occupation;
    monthlyIncome?: string;
    monthlyExtraSupport?: string;
    usageMonthly?: string;
    maximumTx?: string;
    beneficialOwner?: string;
}

/** Un domicilio que Fiado ya tiene guardado. */
export interface SavedAddress {
    etiqueta: string;
    esPrincipal: boolean;
    zipCode?: string;
}

/**
 * Un beneficiario ya registrado en el expediente Fiado.
 *
 * Los campos SUELTOS viajan además del nombre armado porque corregir un reparto exige reenviarlo
 * entero: sin ellos, tocarle el porcentaje a uno obligaría a recapturar a todos.
 */
export interface SavedBeneficiary {
    nombreCompleto: string;
    relacion?: string;
    porcentaje: number;
    nombres?: string;
    apellidoPaterno?: string;
    apellidoMaterno?: string;
    fechaNacimiento?: string;
    telefono?: string;
}

/** Un candidato del buscador (`address-search`), listo para guardarse tal cual. */
export interface AddressCandidate {
    etiqueta: string;
    domicilio: Record<string, unknown>;
}

export interface Beneficiary {
    firstName: string;
    lastNamePaternal: string;
    lastNameMaternal: string;
    birthDate: string;
    relationship: string; // enum Fiado (9) — TD: tipar al enum de type-kit
    sameTitularPhone: boolean;
    phone?: string;
    /** Del total del saldo. Ausente = va solo y se lleva el 100%. El reparto tiene que cerrar en 100. */
    percentage?: number;
}

/**
 * Datos comerciales que describen un MODELO de equipo. Los define `retail-catalog`; ni el front ni
 * el wizard los inventan. Base común del ítem del catálogo y del equipo ya reservado.
 *
 * `eligibleFinanciers` conserva el nombre del catálogo a propósito. Antes el front lo llamaba
 * `altFinanciers` y el renombre silencioso lo dejaba en `undefined` cada vez que alguien leía el
 * dato sin pasar por el mapeo — un `undefined.length` esperando a pasar.
 */
/** Niveles del catálogo de planes. Vacío en una promoción = alcanza a cualquiera. */
export type PlanLevel = 'BRONZE' | 'SILVER' | 'GOLD';

/** Qué mueve una promoción. Los cuatro son opcionales: una promo declara al menos uno. */
export interface PromotionEffect {
    /** Puntos que le resta al enganche mínimo (`-0.10` = diez puntos menos). */
    downPaymentDeltaPct?: number | null;
    /** Bono que se abona a la tarjeta PCF al activar el crédito. */
    pcfBonusCents?: number | null;
    /** Reemplaza la tasa anual del plan (`1.5` = 150 %). */
    tnaOverride?: number | null;
    /** Sube el techo de monto financiable. */
    maxAmountOverrideCents?: number | null;
}

/** Una promoción viva que nombra a este equipo, tal como la muestra el panel del paso 03. */
export interface SkuPromotion {
    promotionId: string;
    name: string;
    effect: PromotionEffect;
    /**
     * Niveles que alcanza. **Vacío = cualquiera.** El nivel del cliente recién sale en el paso 05,
     * así que el panel las agrupa en vez de prometer una que quizá no le toque.
     */
    targetLevels: PlanLevel[];
}

export interface ProductCommercialInfo {
    sku: string;
    brand: string;
    model: string;
    /** Nombre visible del kit o servicio; ausente si el SKU no lo trae. */
    description?: string;
    capacity: string;
    color: string;
    cashPriceCents: number;
    imageUrl: string | null;
    /** Gama del equipo (`PREMIUM` / `HIGH` / `MID` …). */
    tier: string;
    /** Financieras alternativas elegibles (derivación externa, paso 04). */
    eligibleFinanciers: string[];
    /** Promociones vivas que nombran al equipo. `[]` si no hay, o si el catálogo comercial no respondió. */
    promotions?: SkuPromotion[];
}

/**
 * Equipo YA reservado en la sesión: una unidad física concreta, con su IMEI.
 *
 * Es un SNAPSHOT que congela el silo al reservar — el precio de aquí ancla el cobro del paso 07.
 * NO lleva `stock`: la unidad está reservada, así que por definición dejó de estar disponible.
 */
export interface SelectedProduct extends ProductCommercialInfo {
    /** Ausente en sesiones con líneas: el IMEI se elige hasta la entrega. */
    imei?: string;
    /** Línea de `reservedLines` que espeja esta ficha. */
    lineId?: string;
}

/**
 * Estado de la reserva del equipo elegido, tal como lo vive el paso 03.
 *
 * `expired` existe porque tener `product` en la sesión NO significa tener la unidad: a los 30 min
 * el catálogo la recicla y hay que volver a apartarla antes de avanzar.
 */
export type ReservationState = 'none' | 'held' | 'expired';

/** Categoría comercial del producto (`ProductCategoryEnum` del catálogo). `BUNDLE` es un kit. */
export type ProductCategory = `${ProductCategoryEnum}`;

/** Producto físico (suelto o kit) o servicio sin stock ni identificador. */
export type ItemType = `${ItemTypeEnum}`;

/** Cómo se rastrea cada pieza: por IMEI, por número de serie o solo por cantidad. */
export type TrackingMode = `${TrackingModeEnum}`;

/** Componente de un kit: el SKU y las piezas que consume cada kit. */
export type KitComponent = ProductComponent;

/** Componente de un kit ya apartado, con cómo se rastrean sus piezas. */
export interface ReservedComponent extends KitComponent {
    category: ProductCategory;
    trackingMode: TrackingMode;
}

/** Estado de una línea de la venta. `RELEASING` = se pidió quitarla y el catálogo no confirmó. */
export type LineStatus = `${WizardLineStatusEnum}`;

/** Cómo se paga una línea: financiada con SureKeep o de contado. */
export type LinePaymentType = `${LinePaymentTypeEnum}`;

/** Identificadores capturados para un SKU de la línea (el componente, si es kit). */
export type LineIdentifiers = WizardLineIdentifiers;

/** Ficha comercial de la línea, congelada al apartar. */
export interface LineProduct {
    brand: string;
    model: string;
    capacity: string | null;
    color: string | null;
    imageUrl: string | null;
    /** `null` = sin gama. */
    tier: string | null;
    category: ProductCategory;
    trackingMode: TrackingMode;
    /** Ausente = PRODUCT (línea anterior a los servicios). */
    itemType?: ItemType;
    /** Nombre de un servicio o producto sin marca ni modelo; ausente si no lo trae. */
    description?: string;
}

/** Papel especial de una línea (`GIFT` = regalo de una promo); sin él es una línea normal. */
export type LineRole = `${WizardLineRoleEnum}`;

/** Promo de venta de la sesión; sin versión mientras Promociones no la revalida. */
export interface SalePromotionRef {
    promotionId: string;
    promotionVersion?: number;
}

/** Campos que escribe Promociones en la línea; neto = basePriceCents × qty − discountCents. */
export interface LinePromotionFields extends Omit<WizardLinePromotionFields, 'role'> {
    role?: LineRole;
}

/** Línea de la venta en curso. Los precios vienen en `null` mientras la línea está `PENDING`. */
export interface ReservedLine extends LinePromotionFields {
    lineId: string;
    sku: string;
    qty: number;
    status: LineStatus;
    /** Solo en LOST: cómo dejó Zentral la pieza. */
    lostReason?: `${InventoryItemStatusEnum}`;
    /** Vencimiento del apartado de esta línea. */
    expiresAt: string;
    /** Precio unitario fijado al apartar (el del kit si es kit). */
    basePriceCents: number | null;
    eligibleFinanciers: string[];
    /** SureKeep puede financiar esta línea. */
    financeable: boolean;
    paymentType: LinePaymentType | null;
    /** Parte financiada congelada al cotizar; ausente = regla vieja (sesión previa o flag apagado). */
    financedCents?: number;
    /** La línea heredó una promo al cambiar la ancla y Promociones aún no la revalida. */
    promotionRevalidationPending?: boolean;
    /** Piezas que la línea suma al tope de la venta. */
    sellItemCost: number | null;
    mdmEnabled: boolean;
    mdmProvider: string | null;
    /** Vacío si la línea no es kit. */
    components: ReservedComponent[];
    identifiers: LineIdentifiers[];
    product: LineProduct | null;
}

/** Body de agregar una línea. Sin `qty` el silo aparta una pieza. */
export interface AddProductRequest {
    sku: string;
    qty?: number;
    /** Línea viva que la nueva reemplaza en la misma operación («Cambiar», «Cambiar por el kit»). */
    replacesLineId?: string;
}

/** Qué soltar: una línea, o el IMEI de una sesión anterior a las líneas. */
export type ReleaseProductTarget = { lineId: string } | { imei: string };

/** Términos de la cotización. La línea financiada la fija el servidor. */
export interface QuoteTerms {
    downPaymentCents: number;
    termWeeks: number;
}

/** Reparto provisional del crédito: tope de extras y parte financiada por línea. */
export type FinancingPreview = WizardFinancingPreview;

/** Identificadores de una línea al cerrar la venta; `sku` es el del componente si es kit. */
export type CompleteSaleLine = CompleteSaleLineDto;

/** Línea de `/pieces`: mismos campos que la de complete-sale. */
export type PinPiecesLine = PinSessionPiecesLine;

/**
 * Fila del catálogo: una por SKU (producto suelto o kit), con su disponible. Los campos de
 * categoría, rastreo y kit solo faltan en datos de prueba anteriores a las líneas.
 */
export interface CatalogItem extends ProductCommercialInfo {
    /** RAM en GB como texto («8»); `null` o ausente si el SKU no la trae. */
    ram?: string | null;
    /** Ausente = PRODUCT (catálogo anterior a los servicios). */
    itemType?: ItemType;
    /** Piezas disponibles; `null` en un servicio, que no lleva inventario. */
    stock: number | null;
    category?: ProductCategory;
    trackingMode?: TrackingMode;
    /** Solo si es kit. */
    components?: KitComponent[];
    /** SureKeep puede financiarlo. */
    financeable?: boolean;
    priorityRank: number;
    /** Prioridad de venta 0–100 del catálogo; manda en «Recomendados» (mayor primero). */
    salePriority?: number;
    hasPromo: boolean;
    /** Si el MODELO se vende con candado remoto. Es política del producto, no estado del aparato. */
    mdmEnabled: boolean;
    /** Proveedor del candado (`MdmProviderEnum`). `null` cuando el modelo no lo lleva. */
    mdmProvider: string | null;
    /** Cuándo se dispara el bloqueo (`MdmLockModeEnum`). `null` cuando el modelo no lo lleva. */
    mdmLockMode: string | null;
    /**
     * Con el score adelantado y el nivel del cliente ya resuelto: si el equipo se financia con el
     * plan de ese nivel. `undefined` sin nivel aún, NOT_ELIGIBLE, o sin plan sembrado — nunca
     * esconde el equipo, solo apaga la marca (decisión: marcar, no filtrar).
     */
    affordableOnCredit?: boolean;
    /** Enganche mínimo en centavos cuando el precio pasa el tope del nivel. Referencia, no exacto. */
    minDownPaymentCents?: number;
    /** Enganche mínimo del plan del nivel (decimal sobre 1), cuando la fila lo trae. */
    minDownPaymentPct?: number;
    /** Tope de financiamiento del nivel, cuando la fila lo trae. */
    creditCapCents?: number;
}

/**
 * Promoción que NO acota por SKU (tienda, nivel, segmento o universal) — alimenta el banner de
 * pantalla completa del paso 03. El backend ya resuelve el contexto (tienda/segmento/nivel) del
 * lado del silo, el front solo la pinta.
 */
export interface ScopeWidePromotion {
    promotionId: string;
    name: string;
    description: string;
    effect: PromotionEffect;
    /** Segmento al que apunta. El segmento por omisión alcanza a cualquier cliente. */
    segmentId: string;
    /** Niveles que alcanza. Vacío = cualquiera. */
    targetLevels: PlanLevel[];
}

export interface CatalogResponse {
    items: CatalogItem[];
    /** Local-first: SIEMPRE responde del silo; banner si `syncedAt` viejo (staleness). */
    syncedAt: string;
    /**
     * `[]` sin promociones vigentes, o si el backend todavía no manda este campo (respuesta en
     * array plano) — el catálogo por SKU sigue funcionando igual.
     */
    scopeWidePromotions: ScopeWidePromotion[];
    /** Tope de financiamiento del nivel del cliente; solo llega con nivel resuelto. */
    creditCapCents?: number;
    /** Enganche mínimo del plan del nivel (decimal sobre 1); ausente mientras el silo no lo mande. */
    minDownPaymentPct?: number;
}

export interface RegisteredPayment {
    /** Medio del primer pago; `null` en un cobro de $0. Con varios, el desglose está en `payments`. */
    method: PaymentMethod | null;
    amountCents: number;
    /** Pagos del cobro; `null` en sesiones cobradas antes del campo. */
    payments?: PaymentLine[] | null;
    detail: {
        cashConfirmedInRegister?: boolean;
        cardLast4?: string;
        authCode?: string;
        speiTrackingKey?: string;
        senderClabe?: string;
    };
}

// ── Prospectos (F2-P — drawer transversal 02–09) ─────────────────────────────
// Espejo del dominio `retailWizard` de `@fiado/type-kit` (el front no importa la lib: declara sus
// tipos a mano). El silo valida con `excludeExtraneousValues`: todo campo que el DTO no declara se
// descarta en silencio.

/** `ProspectStatusEnum`. El silo crea todo prospecto como `PENDING`; `CLOSED` lo pone la venta. */
export type ProspectStatus =
    | 'PENDING'
    | 'UNDER_REVIEW'
    | 'READY_TO_RESUME'
    | 'CLOSED'
    | 'CANCELLED'
    | 'LOST';

/**
 * `PendingReasonEnum` — los 17 motivos del catálogo. Uno fuera de la lista lo rechaza `@IsEnum`.
 * Los dos `ACCOUNT_*` los pone el silo al trabarse la venta; el vendedor elige entre los otros 15.
 */
export type PendingReason =
    | 'OTP_FAILED'
    | 'NO_INE'
    | 'NO_OCCUPATION'
    | 'DISLIKES_CATALOG'
    | 'PRICE_HIGH'
    | 'EVALUATING_CONDITIONS'
    | 'REJECTED_RETRY'
    | 'INSTALLMENT_HIGH'
    | 'EVALUATING_DOWNPAYMENT'
    | 'EVALUATING_TERM'
    | 'NO_MONEY_TODAY'
    | 'CONTRACT_DOUBTS'
    | 'DEVICE_ISSUE'
    | 'CLIENT_THINKING'
    | 'OTHER'
    | 'ACCOUNT_UNDER_REVIEW'
    | 'ACCOUNT_OPEN_FAILED'
    | 'ACCOUNT_REJECTED';

/** Captura manual del cliente que todavía no existe en Fiado. */
export interface ProspectNewCustomer {
    name: string;
    phone: string;
}

/**
 * `GET /wizard/sessions?phone=` — la venta en curso de un teléfono, para la pestaña Wizard de
 * Prospectos. Espejo de `ActiveSessionByPhoneResponse` del type-kit (el front declara sus tipos a
 * mano), ya traducido al vocabulario de las pantallas: `currentStep` sin el prefijo `S` del silo.
 *
 * `found: false` es la respuesta NORMAL, no un error: la mayoría de los prospectos no tiene venta
 * empezada, y una sesión activa de otra tienda llega igual (DEC-018).
 */
export interface ActiveWizardSession {
    found: boolean;
    sessionId: string | null;
    /** `null` sin sesión, y también cuando el silo manda un paso fuera del catálogo de los 10. */
    currentStep: WizardStepId | null;
    state: WizardSessionState | null;
    /** El bloqueo de la venta. Ausente cuando va bien, y también cuando `found` es `false`. */
    blocked?: WizardBlockedSummary;
    /** Pantalla exacta a la que entra «Reabrir». `null` = entra por `resumeTargetStep`. */
    resumeSlug?: WizardStepSlug | null;
    /** Paso al que entra «Reabrir» sin pantalla exacta (checkpoint del silo). */
    resumeTargetStep?: WizardStepId | null;
    /** Hasta cuándo se retoma exacta (ISO). `null` = no vence; ausente = el silo no lo dijo. */
    exactUntil?: string | null;
    /** Movió dinero u originó crédito: se retoma siempre la misma venta. */
    moneyAtStake?: boolean;
    /** Qué dinero retiene: `SALE` (folio, cobro o venta) o `CREDIT_QUOTED`; `null` = ninguno. */
    moneyAtStakeKind?: MoneyAtStakeKind | null;
    /** Venta solo de contado: el cliente tiene una venta con dinero en otra sucursal. */
    cashOnly?: boolean;
}

/** Qué dinero retiene la venta en curso de un teléfono. */
export type MoneyAtStakeKind = `${MoneyAtStakeKindEnum}`;

/**
 * `GET /prospects/by-phone?phone=` — lo que el drawer precarga del prospecto abierto de ese
 * teléfono. Espejo de `ProspectByPhoneResponse` (TD-F2-WIZ-001). `found: false` llega en 200.
 */
export interface ProspectByPhone {
    found: boolean;
    customerName: string | null;
    /** `null` también cuando el silo manda un motivo que este catálogo todavía no conoce. */
    pendingReason: PendingReason | null;
    freeReason: string | null;
    nextContactAt: string | null;
    notes: string | null;
}

export type ProspectResumeMode = 'EXACT' | 'CHECKPOINT' | 'NEW';

/** `POST /prospects/{id}/resume`. Espejo de `ProspectResumeResponse` del type-kit. */
export interface ProspectResume {
    sessionId: string | null;
    mode: ProspectResumeMode;
}

export interface Prospect {
    prospectId: string;
    retailerId: string;
    storeId: string;
    sellerRetailUserId: string;
    /** Identificado vs captura manual: viene uno de los dos, nunca los dos. */
    retailCustomerId: string | null;
    newCustomer: ProspectNewCustomer | null;
    productSku: string | null;
    pendingReason: PendingReason;
    /** El silo lo fuerza a `''` cuando el motivo es OTHER y no llega texto. */
    freeReason: string | null;
    /** Instante ISO completo — el GSI del silo ordena la agenda por este string. */
    nextContactAt: string;
    notes: string | null;
    status: ProspectStatus;
    /** La venta de la que salió. `null` en los prospectos guardados antes de que el drawer lo mandara. */
    wizardSessionId: string | null;
    saleId: string | null;
    lastContactAt: string | null;
    createdBy: string;
    updatedBy: string;
    createdAt: number;
    updatedAt: number;
}

// ── Requests (bodies que arma el front) ──────────────────────────────────────
export interface CreateSessionRequest {
    phone: string;
    storeId: string;
    sellerId: string;
    sku?: string; // deep-link ?sku=
    prospectId?: string; // deep-link ?prospect= (reanudar)
    /** Apertura de cuenta sin venta; nunca viaja junto con `prospectId`. */
    accountOpening?: true;
}

export interface CreateSessionResponse {
    sessionId: string;
    channelsDispatched: string[];
    otpExpiresAt: string;
    resendAvailableAt: string;
    phoneStatus: PhoneStatus;
    firstName?: string;
    /** Eco de la marca de apertura; un back que no la conoce no la manda. */
    accountOpening?: boolean | null;
}

export interface KycPrepareResponse {
    modality: KycModality;
    /** STORE_DEVICE: config del widget SaaS de Metamap (única excepción a la regla puente). */
    clientId?: string;
    flowId?: string;
    metadata?: string;
    /** CUSTOMER_LINK: shortUrl para el QR. */
    shortUrl?: string;
    /** Cliente recurrente con KYC vigente → la pantalla 02 se salta. */
    alreadyVerified?: boolean;
    /** Intento que abrió este prepare; el front liga el SDK y el QR a él. Ausente si no hubo intento. */
    attemptId?: string;
}

/** Body del cobro de contado y del enganche: el total y sus pagos (sin pagos si es $0). */
export type RegisterPaymentRequest = Pick<
    components['schemas']['RegisterPaymentRequest'],
    'amountCents' | 'payments'
>;

export interface CreateProspectRequest {
    /** Cliente ya identificado en Fiado. Excluyente con `newCustomer`. */
    retailCustomerId?: string;
    newCustomer?: ProspectNewCustomer;
    productSku?: string;
    pendingReason: PendingReason;
    /** Obligatorio de negocio si `pendingReason` es OTHER; ≤200. */
    freeReason?: string;
    /** Instante ISO completo. Sin él, el silo agenda para mañana. */
    nextContactAt?: string;
    notes?: string;
    /** La venta de la que sale el prospecto: el único camino de vuelta del prospecto a su sesión. */
    wizardSessionId?: string;
    /** Pantalla a la que vuelve la venta al reanudar. El silo lo ignora sin `wizardSessionId`. */
    resumeSlug?: WizardStepSlug;
}

export interface MarkProspectLostRequest {
    reason: string;
}
